<template>
  <div class="page-container">
    <!-- 헤더 -->
    <div class="page-header">
      <div>
        <div class="page-title">
          전체 계정 리스트
          <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
        </div>
      </div>

      <div class="header-buttons">
        <button class="btn primary" @click="onClickCreate">
          신규 등록
        </button>
      </div>
    </div>

    <!-- 본문 -->
    <div class="content-wrapper">
      <div class="card account-management-card">
        <!-- 필터 영역 -->
        <div class="filter-card">
          <div class="filter-row filter-row-main">
            <div class="filter-field">
              <label class="filter-label">대리점 선택</label>
              <select v-model="filters.agencyId" class="control">
                <option :value="null">대리점을 선택하세요</option>
                <option
                  v-for="agency in agencyOptions"
                  :key="agency.id"
                  :value="agency.id"
                >
                  {{ agency.name }}
                </option>
              </select>
            </div>

            <div class="filter-field">
              <label class="filter-label">발주처 선택</label>
              <select v-model="filters.clientId" class="control">
                <option :value="null">발주처를 선택하세요</option>
                <option
                  v-for="client in clientOptions"
                  :key="client.id"
                  :value="client.id"
                >
                  {{ client.name }}
                </option>
              </select>
            </div>

            <div class="filter-field filter-field-keyword">
              <label class="filter-label">직접 입력</label>
              <input
                v-model.trim="filters.usernameKeyword"
                class="control"
                type="text"
                placeholder="ID를 입력하세요"
                @keyup.enter="onClickSearch"
              />
            </div>

            <div class="filter-actions">
              <button class="btn primary search-btn" @click="onClickSearch">
                검색
              </button>

              <button class="btn ghost reset-btn" @click="onClickReset">
                초기화
              </button>
            </div>
          </div>
        </div>

        <!-- 테이블 -->
        <div class="table-section">
          <table v-if="tableRows.length" class="data-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>권한</th>
                <th>ID</th>
                <th>대리점</th>
                <th>발주처</th>
                <th>상태</th>
                <th>최근접속일</th>
                <th>메모</th>
                <th class="text-center">관리</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tableRows" :key="row.id">
                <td>{{ row.type || '-' }}</td>

                <td>{{ row.role || '-' }}</td>

                <td class="td-ellipsis" :title="row.accountId">
                  {{ row.accountId || '-' }}
                </td>

                <td class="td-ellipsis" :title="row.agencyName">
                  {{ row.agencyName || '-' }}
                </td>

                <td class="td-ellipsis" :title="row.clientName">
                  {{ row.clientName || '-' }}
                </td>

                <td>
                  <span
                    class="badge"
                    :class="row.status === '정상' ? 'success' : 'danger'"
                  >
                    {{ row.status || '-' }}
                  </span>
                </td>

                <td>{{ formatDateTime(row.lastLoginAt) || '-' }}</td>

                <td class="td-ellipsis" :title="row.memo">
                  {{ row.memo || '-' }}
                </td>

                <td class="td-actions">
                  <div class="row-actions">
                    <button
                      class="btn xs primary"
                      :disabled="!canManage(row)"
                      @click="onClickEdit(row)"
                    >
                      수정
                    </button>

                    <button
                      class="btn xs danger outline"
                      :disabled="!canManage(row)"
                      @click="onClickDelete(row)"
                    >
                      {{ row.status === '정상' ? '삭제' : '복구' }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 빈 상태 -->
          <div v-else class="table-empty">
            표시할 계정 데이터가 없습니다
          </div>
        </div>

        <!-- 페이징 -->
        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'AccountManagement',

  components: {
    PaginationBar
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,
      loading: false,

      agencyOptions: [],
      clientOptions: [],

      filters: {
        agencyId: null,
        clientId: null,
        usernameKeyword: '',
      },

      currentUser: null,
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    }
  },

  async mounted() {
    await this.fetchCurrentUser()

    await Promise.all([
      this.fetchAgencyOptions(),
      this.fetchClientOptions(),
    ])

    await this.fetchAccountList()
  },

  methods: {
    async fetchCurrentUser() {
      try {
        const response = await api.get('/auth/me')
        this.currentUser = response.data || null
      } catch (error) {
        console.error('현재 로그인 사용자 조회 실패', error)
        this.currentUser = null
      }
    },

    getCurrentRoleOrder() {
      const roleCode = this.currentUser?.roleCode

      if (roleCode === 'SUPER_ADMIN') return 1
      if (roleCode === 'ADMIN') return 2
      if (roleCode === 'OPERATOR') return 3
      if (roleCode === 'USER') return 4
      if (roleCode === 'MONITOR') return 5

      return 999
    },

    canManage(row) {
      const currentOrder = this.getCurrentRoleOrder()
      const targetOrder = Number(row.roleSortOrder ?? 999)

      return currentOrder <= targetOrder
    },

    async fetchAgencyOptions() {
      try {
        const response = await api.get('/app/agency/list', {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: 'nameAsc'
          }
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.agencyOptions = Array.isArray(content) ? content : []
      } catch (error) {
        console.error('대리점 목록 조회 실패', error)
        this.agencyOptions = []
      }
    },

    async fetchClientOptions() {
      try {
        const response = await api.get('/app/client/list', {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: 'nameAsc'
          }
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.clientOptions = Array.isArray(content) ? content : []
      } catch (error) {
        console.error('발주처 목록 조회 실패', error)
        this.clientOptions = []
      }
    },

    async fetchAccountList() {
      this.loading = true

      try {
        const params = {
          page: this.currentPage,
          size: this.size,
          orderBy: 'createdAtDesc',
        }

        if (this.filters.agencyId) {
          params.agencyId = this.filters.agencyId
        }

        if (this.filters.clientId) {
          params.clientId = this.filters.clientId
        }

        if (this.filters.usernameKeyword) {
          params.usernameLike = this.filters.usernameKeyword
        }

        const res = await api.get('/app/accountUser/pagingList', {
          params
        })

        const page = res.data?.result

        this.tableRows = page?.content || []
        this.totalCount = page?.totalElements || 0
        this.totalPages = page?.totalPages || 0
        this.currentPage = page?.number || 0

      } catch (e) {
        console.error(e)
        this.tableRows = []
      } finally {
        this.loading = false
      }
    },

    onClickSearch() {
      this.currentPage = 0
      this.fetchAccountList()
    },

    onClickReset() {
      this.filters.agencyId = null
      this.filters.clientId = null
      this.filters.usernameKeyword = ''
      this.currentPage = 0
      this.fetchAccountList()
    },

    async onClickDelete(row) {
      const isDelete = row.status === '정상'

      if (isDelete) {

        const firstOk = confirm(
          `"${row.accountId}" 계정을 삭제하시겠습니까?\n\n삭제 후 목록에서 표시되지 않습니다.`
        )

        if (!firstOk) return

        const secondOk = confirm(
          `"${row.accountId}" 계정을 정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.`
        )

        if (!secondOk) return

      } else {

        const restoreOk = confirm(
          `"${row.accountId}" 계정을 복구하시겠습니까?`
        )

        if (!restoreOk) return
      }

      try {
        await api.post('/app/accountUser/updateStatus', {
          id: row.id,
          deleted: isDelete
        })

        alert(isDelete ? '삭제 완료' : '복구 완료')

        await this.fetchAccountList()

      } catch (e) {
        console.error(e)
        alert('처리 실패')
      }
    },

    onClickCreate() {
      this.$emit('create')
    },

    onClickEdit(row) {
      this.$emit('edit', row)
    },

    goToPage(page) {
      this.currentPage = page
      this.fetchAccountList()
    },

    formatDateTime(v) {
      if (!v) return ''

      const d = new Date(v)
      if (isNaN(d.getTime())) return v

      const yy = String(d.getFullYear()).slice(-2)
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mi = String(d.getMinutes()).padStart(2, '0')
      const ss = String(d.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-pagination.css";
@import "@/styles/admin/admin-badge.css";
@import "@/styles/admin/admin-input.css";

.account-management-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* =========================
   FILTER CARD
========================= */
.filter-card {
  margin-bottom: 20px;
  padding: 18px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f8fafc;
  box-sizing: border-box;
  overflow: visible;
}

/* 대리점 / 발주처 / 직접입력 / 검색 / 초기화 한 줄 구성 */
.filter-row-main {
  display: grid;
  grid-template-columns:
    minmax(180px, 260px)
    minmax(180px, 260px)
    minmax(220px, 1fr)
    88px
    88px;
  gap: 12px;
  align-items: end;
  width: 100%;
  min-width: 0;
}

.filter-field {
  min-width: 0;
}

.filter-field-keyword {
  min-width: 220px;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  line-height: 1.2;
}

.filter-actions {
  display: contents;
}

.search-btn,
.reset-btn {
  width: 88px;
  min-width: 88px;
  height: 40px;
  white-space: nowrap;
}

/* 예전 구조 class 영향 제거 */
.filter-row,
.filter-row-top,
.filter-row-bottom {
  min-width: 0;
}

.keyword-search-wrap,
.search-action-group {
  min-width: 0;
}

/* =========================
   TABLE
========================= */
.table-section {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.data-table {
  width: 100%;
  min-width: 1180px;
  table-layout: fixed;
}

/* 전 컬럼 말줄임 금지, 긴 텍스트는 줄바꿈 */
.data-table th,
.data-table td {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.45;
  vertical-align: middle;
}

/* 마우스 hover 시 행 active 효과 */
.data-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.data-table tbody tr:hover td {
  background: #f8fafc;
}

.data-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}

/* 기존 ellipsis class 무력화 */
.td-ellipsis {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 셀별 가로 폭 조정 */
.data-table th:nth-child(1),
.data-table td:nth-child(1) {
  width: 90px;
}

.data-table th:nth-child(2),
.data-table td:nth-child(2) {
  width: 100px;
}

.data-table th:nth-child(3),
.data-table td:nth-child(3) {
  width: 150px;
}

.data-table th:nth-child(4),
.data-table td:nth-child(4) {
  width: 140px;
}

.data-table th:nth-child(5),
.data-table td:nth-child(5) {
  width: 140px;
}

.data-table th:nth-child(6),
.data-table td:nth-child(6) {
  width: 90px;
}

.data-table th:nth-child(7),
.data-table td:nth-child(7) {
  width: 170px;
}

.data-table th:nth-child(8),
.data-table td:nth-child(8) {
  width: 240px;
}

.data-table th:nth-child(9),
.data-table td:nth-child(9) {
  width: 170px;
}

/* 관리 버튼 */
.td-actions {
  text-align: center;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  flex-wrap: nowrap;
  white-space: nowrap;
}

.table-empty {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* =========================
   RESPONSIVE
========================= */
@media (max-width: 1280px) {
  .filter-row-main {
    grid-template-columns: 1fr 1fr;
  }

  .filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .filter-row-main {
    grid-template-columns: 1fr;
  }

  .filter-field-keyword {
    min-width: 0;
  }

  .filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    grid-column: auto;
  }
}

@media (max-width: 768px) {
  .data-table {
    min-width: 1280px;
  }
}
</style>